import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { Odometer } from '../../components/ui/Odometer';
import { Icon } from '../../components/ui/Icon';
import { fmtEur, fmtDate, fmtDateShort } from '../../lib/utils';
import { canReadAdmin } from '../admin/adminApi';
import { ActionStream } from './ActionStream';
import { useActionStream } from './useActionStream';
import {
  oggiQuery, FONTE_LABEL, NATURA_LABEL,
  type Natura, type OggiPeriodo, type OggiRigaFattura, type OggiRigaPagamento, type OggiSummary,
} from './oggiApi';
import './soldi.css';

/**
 * Oggi — la home. E' una pagina di SOLDI.
 *
 * L'ordine non e' una scelta grafica, e' la lista che ha dettato Lorenzo, presa
 * dall'alto in basso:
 *   1. quanta liquidita' c'e' adesso
 *   2. quanto ci aspettiamo di fare questo mese, entrate e uscite separate
 *   3. quanto il mese prossimo
 *   4. quanto in un intervallo che sceglie lui
 *   5. quanto vale il lavoro in mano e quello che stiamo ancora vendendo
 *   6. le fatture in scadenza e quelle gia' scadute
 *   7. tutto quello che c'e' da pagare
 * Le cose da fare restano, in fondo: erano il protagonista e non lo sono piu'.
 *
 * DUE REGOLE che si vedono in ogni riga.
 *
 * Nessun numero senza la sua natura. Accanto a ogni cifra c'e' scritto se e'
 * un FATTO (soldi gia' passati dal conto), una cosa CERTA (un documento esiste:
 * l'importo e' quello, la data puo' slittare) o una PREVISIONE (canoni
 * proiettati, voci a mano, valore delle trattative). Un cruscotto che mescola
 * le tre cose in un totale unico si legge una volta e poi non ci si crede piu'.
 *
 * Nessun conto fatto qui. Tutte le somme arrivano da GET /admin/oggi
 * (apps/api/src/modules/admin/admin.oggi.ts), comprese quelle che sembrano
 * banali: il browser ha in mano una risposta, non il database. La sezione
 * "Come non contiamo due volte" mostra, in euro, quanto e' stato tolto per non
 * far comparire lo stesso euro in due caselle.
 *
 * Stato in URL: da · a (l'intervallo scelto), cosi' un periodo si condivide.
 */

const PRESET = [
  { key: '30', label: '30 giorni', giorni: 30 },
  { key: '90', label: '90 giorni', giorni: 90 },
  { key: '180', label: '6 mesi', giorni: 180 },
  { key: '365', label: '12 mesi', giorni: 365 },
] as const;

/** "YYYY-MM-DD" di oggi piu' n giorni, senza passare dal fuso del browser. */
function ymdPlus(base: string, giorni: number): string {
  const d = new Date(`${base}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + giorni);
  return d.toISOString().slice(0, 10);
}

/** Lo stesso tetto che applica il server (MAX_GIORNI in admin.oggi.ts). */
const MAX_GIORNI = 400;
const ISO = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Cosa non va nel periodo scelto, in italiano, oppure null.
 *
 * Serve a non mandare al server una richiesta che si sa già rifiutata: il
 * server risponderebbe 400 e la pagina perderebbe TUTTI i numeri, liquidità
 * compresa, per una data battuta male. Qui invece si tiene l'ultimo periodo
 * buono e si dice cosa correggere. Un indirizzo si può sempre incollare a
 * mano, quindi il caso va previsto, non sperato.
 */
function problemaDelPeriodo(da: string, a: string): string | null {
  if (!da && !a) return null;
  if ((da && !ISO.test(da)) || (a && !ISO.test(a))) return 'Una delle due date non è scritta bene.';
  if (da && a) {
    if (da > a) return 'La data di fine viene prima di quella di inizio.';
    const giorni = Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${da}T00:00:00Z`)) / 86_400_000) + 1;
    if (giorni > MAX_GIORNI) return `Il periodo supera ${MAX_GIORNI} giorni: più in là la proiezione dei canoni non vuol dire più niente.`;
  }
  return null;
}

export function OggiPage() {
  const user = useAuthStore((s) => s.user);
  const canAdmin = canReadAdmin(user);
  const { counts } = useActionStream();

  // L'intervallo del punto 4. Vuoto = il server decide il default (i due mesi
  // gia' in pagina), cosi' l'URL resta pulito finche' non si sceglie davvero.
  //
  // Le due date si scrivono in UNA sola chiamata a setSearchParams, mai in due.
  // Con due chiamate nello stesso giro di eventi la seconda parte dai parametri
  // che vede nella location, che React non ha ancora aggiornato, e cancella la
  // prima: misurato, premendo "30 giorni" finiva in URL solo `a`, e "Azzera"
  // lasciava `da` orfano con un `a` piu' vecchio — cioe' un intervallo alla
  // rovescia, 400 dal server e pagina che spariva. E' la stessa ragione per cui
  // DealsPage ha `setParamsBatch`.
  const [params, setParams] = useSearchParams();
  const da = params.get('da') ?? '';
  const a = params.get('a') ?? '';
  const setPeriodo = (nuovoDa: string, nuovoA: string) => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (nuovoDa) sp.set('da', nuovoDa); else sp.delete('da');
      if (nuovoA) sp.set('a', nuovoA); else sp.delete('a');
      return sp;
    });
  };

  // Un periodo impossibile il server lo rifiuta, giustamente. Ma dall'URL o da
  // una data battuta a mano ci si arriva in un attimo: si avvisa e si restano
  // sui numeri buoni, invece di far sparire la pagina dei soldi.
  const problema = problemaDelPeriodo(da, a);

  const { data, isLoading, isError } = useQuery({
    ...oggiQuery(problema ? undefined : da || undefined, problema ? undefined : a || undefined),
    enabled: canAdmin,
    retry: 1,
  });

  const oggi = data?.today ?? new Date().toISOString().slice(0, 10);
  const dataLunga = new Date(`${oggi}T00:00:00.000Z`).toLocaleDateString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  });

  const setPreset = (giorni: number) => setPeriodo(oggi, ymdPlus(oggi, giorni - 1));

  return (
    <div className="page stagger">
      <div className="page-ed-hd">
        <h1 className="ed-title">Ciao, <strong>{user?.firstName}</strong>.</h1>
        <div className="ed-sub flex items-baseline gap-2.5 flex-wrap">
          <span>{dataLunga.charAt(0).toUpperCase() + dataLunga.slice(1)}</span>
          {counts.critical > 0 && (
            <>
              <span className="mute-2">·</span>
              <span className="text-alert font-semibold">
                {counts.critical} azion{counts.critical === 1 ? 'e critica' : 'i critiche'}
              </span>
            </>
          )}
        </div>
      </div>

      {!canAdmin ? (
        <NoSoldi />
      ) : isError ? (
        <div className="card"><div className="card-bd text-[13px] mute">
          L'Amministrazione non risponde: i numeri arrivano appena l'API è su. Le cose da fare restano qui sotto.
        </div></div>
      ) : !data ? (
        <Scheletro loading={isLoading} />
      ) : (
        <>
          {/* 1 — LIQUIDITÀ, e accanto le due domande successive in sintesi */}
          <Testata d={data} />

          {/* 2, 3, 4 — i tre periodi, entrate e uscite separate */}
          <div className="ed-divider" />
          <Periodi d={data} da={da} a={a} setPeriodo={setPeriodo} setPreset={setPreset} oggi={oggi} problema={problema} />

          {/* 5 — lavoro in mano e lavoro in vendita, più la prova dei conti */}
          <div className="ed-divider" />
          <Lavoro d={data} />

          {/* 6 e 7 — fatture e pagamenti, affiancati */}
          <div className="ed-divider" />
          <div className="adm-bento">
            <Fatture d={data} />
            <DaPagare d={data} />
          </div>
        </>
      )}

      {/* Le cose da fare: restano, ma sotto i soldi */}
      <div className="ed-divider" />
      <div className="data-hd">
        <span className="t-micro">Da fare</span>
        <span className="mute t-small mono">{counts.critical + counts.today + counts.week} da gestire</span>
      </div>
      <div className="card">
        <div className="card-bd card-bd--tight"><ActionStream /></div>
      </div>
    </div>
  );
}

// ─── Pezzi ──────────────────────────────────────────────────────────────────

/** Il numero con l'unità davanti e il segno meno vero (−, non un trattino). */
function Euro({ value, animate }: { value: number; animate?: boolean }) {
  const neg = value < 0;
  return (
    <>
      {neg && '−'}
      <span className="u">€</span>
      {animate ? <AnimatedNumber value={Math.abs(value)} format={fmtEur} /> : fmtEur(Math.abs(value))}
    </>
  );
}

/** "fatto", "certo", "previsione": la natura del numero accanto al numero. */
function Tag({ natura }: { natura: Natura }) {
  return <em className="og-tag">{NATURA_LABEL[natura]}</em>;
}

/**
 * La data di una scadenza in poco spazio.
 *
 * "16 mar" va bene finché si resta nell'anno in corso. Le liquidazioni IVA
 * arretrate però sono del 2023, del 2024 e del 2025: in colonna, ordinate
 * correttamente per data vera, si leggevano "16 mar · 18 nov · 16 mar · 16
 * mag" e sembravano messe a caso. Fuori dall'anno corrente l'anno si scrive.
 */
function scadenza(iso: string | null, annoOggi: number): string {
  if (!iso) return '—';
  const anno = Number(iso.slice(0, 4));
  if (!Number.isFinite(anno)) return '—';
  if (anno === annoOggi) return fmtDateShort(iso);
  // iso è "YYYY-MM-DD": si prendono i pezzi per posizione, senza destrutturare
  // (con noUncheckedIndexedAccess gli elementi di split() sono possibly undefined)
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}

/** "alle 10:32" se è di oggi, altrimenti la data. */
function quando(iso: string | null): string {
  if (!iso) return 'mai aggiornato';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'mai aggiornato';
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return d.toDateString() === new Date().toDateString() ? `aggiornato alle ${hm}` : `aggiornato il ${fmtDate(d)}`;
}

/**
 * Punto 1 in evidenza e, sulla stessa fascia, le tre risposte che servono per
 * decidere se quella liquidità basta: saldo atteso dei due mesi e quanto c'è
 * da tirare fuori. I numeri stanno nudi sul foglio, separati da hairline.
 */
function Testata({ d }: { d: OggiSummary }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="data-hd">
        <span className="t-micro">Quanti soldi abbiamo</span>
        <Link to="/admin" className="btn btn-sm btn-ghost">Amministrazione <Icon name="chevronR" size={12} /></Link>
      </div>
      <div className="stat-band og-band4">
        <div>
          <div className="st-label">Liquidità adesso</div>
          <div className="st-value"><span className="u">€</span><Odometer value={d.liquidita.totale} format={fmtEur} /></div>
          <div className="st-delta">
            banca € {fmtEur(d.liquidita.banca)} · contanti € {fmtEur(d.liquidita.contanti)} · {quando(d.liquidita.aggiornatoAl)}
          </div>
        </div>
        <div className="st-click" role="button" tabIndex={0} onClick={() => navigate('/admin?tab=panoramica')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/admin?tab=panoramica'); }}>
          <div className="st-label">Saldo atteso questo mese</div>
          <div className={`st-value${d.questoMese.saldo < 0 ? ' text-alert' : ''}`}><Euro value={d.questoMese.saldo} animate /></div>
          <div className="st-delta">entrate € {fmtEur(d.questoMese.entrate.totale)} · uscite € {fmtEur(d.questoMese.uscite.totale)}</div>
        </div>
        <div className="st-click" role="button" tabIndex={0} onClick={() => navigate('/admin?tab=panoramica')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/admin?tab=panoramica'); }}>
          <div className="st-label">Il mese prossimo</div>
          <div className={`st-value${d.prossimoMese.saldo < 0 ? ' text-alert' : ''}`}><Euro value={d.prossimoMese.saldo} animate /></div>
          <div className="st-delta">entrate € {fmtEur(d.prossimoMese.entrate.totale)} · uscite € {fmtEur(d.prossimoMese.uscite.totale)}</div>
        </div>
        <div className="st-click" role="button" tabIndex={0} onClick={() => navigate('/admin?tab=uscite')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/admin?tab=uscite'); }}>
          <div className="st-label">Da pagare in tutto</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={d.daPagare.totale} format={fmtEur} /></div>
          <div className={`st-delta${d.daPagare.documenti.scaduto > 0 ? ' text-alert' : ''}`}>
            {d.daPagare.documenti.scaduto > 0 ? `€ ${fmtEur(d.daPagare.documenti.scaduto)} già scaduti` : 'niente in ritardo'}
          </div>
        </div>
      </div>
    </>
  );
}

/** Punti 2, 3 e 4: le tre colonne, e il selettore che ricalcola la terza. */
function Periodi({
  d, da, a, setPeriodo, setPreset, oggi, problema,
}: {
  d: OggiSummary; da: string; a: string;
  setPeriodo: (da: string, a: string) => void;
  setPreset: (giorni: number) => void; oggi: string; problema: string | null;
}) {
  const attivo = useMemo(() => {
    if (!da || !a) return '';
    const giorni = Math.round((new Date(`${a}T00:00:00Z`).getTime() - new Date(`${da}T00:00:00Z`).getTime()) / 86_400_000) + 1;
    return da === oggi ? (PRESET.find((p) => p.giorni === giorni)?.key ?? '') : '';
  }, [da, a, oggi]);

  return (
    <>
      {/* Il selettore sta nell'intestazione della sezione e non dentro la terza
          scheda: messo là dentro le spingeva il contenuto più in basso delle
          altre due, e le tre colonne non si leggevano più in parallelo. Qui è
          allineato sopra la colonna che comanda, e le schede restano identiche. */}
      <div className="data-hd">
        <span className="t-micro">Entrate e uscite</span>
        <div className="og-range">
          <div className="seg-control">
            {PRESET.map((x) => (
              <button key={x.key} type="button" aria-pressed={attivo === x.key} onClick={() => setPreset(x.giorni)}>
                {x.label}
              </button>
            ))}
          </div>
          <div className="og-dates">
            <input type="date" className="inp inp-sm" value={da || d.intervallo.from} max={a || undefined} onChange={(e) => setPeriodo(e.target.value, a)} aria-label="Dal" />
            <span className="mute-2 text-[12px]">→</span>
            <input type="date" className="inp inp-sm" value={a || d.intervallo.to} min={da || undefined} onChange={(e) => setPeriodo(da, e.target.value)} aria-label="Al" />
          </div>
          {(da || a) && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPeriodo('', '')}>Azzera</button>
          )}
        </div>
      </div>
      {problema && (
        <div className="adm-note">
          <strong>{problema}</strong>
          <span>I numeri della terza colonna sono ancora quelli dell'ultimo periodo valido.</span>
        </div>
      )}
      <div className="og-periods">
        <ColonnaPeriodo p={d.questoMese} titolo="Questo mese" />
        <ColonnaPeriodo p={d.prossimoMese} titolo="Il mese prossimo" />
        <ColonnaPeriodo p={d.intervallo} titolo="Periodo scelto" />
      </div>
    </>
  );
}

/**
 * Una colonna: entrate, uscite, saldo. Ogni voce porta la sua natura, e le
 * voci a zero restano scritte invece di sparire — "non ci sono canoni previsti"
 * è un'informazione, una riga che manca è un dubbio.
 */
function ColonnaPeriodo({ p, titolo }: { p: OggiPeriodo; titolo: string }) {
  const e = p.entrate;
  const u = p.uscite;
  return (
    <div className="card oggi-fill">
      <div className="card-bd">
        <div className="og-per-hd">
          <div className="t-h3">{titolo}</div>
          <span className="mute t-small mono">{fmtDateShort(p.from)} → {fmtDateShort(p.to)}</span>
        </div>

        <div className="og-block">
          <div className="og-block-hd">
            <span className="t-micro">Entrate</span>
            <span className="og-block-num"><Euro value={e.totale} /></span>
          </div>
          <Riga k="Già incassate" v={e.incassate} natura="FATTO" />
          <Riga k="Fatture da incassare" v={e.dettaglio.fatture} natura="CERTO" />
          <Riga k="Canoni attivi" v={e.dettaglio.ricorrenti} natura="PREVISIONE" />
          <Riga k="Previsioni a mano" v={e.dettaglio.manuali} natura="PREVISIONE" />
          {e.scaduto > 0 && (
            <div className="og-row">
              <span className="k text-alert">di cui già scaduto</span>
              <span className="n text-alert">€ {fmtEur(e.scaduto)}</span>
            </div>
          )}
        </div>

        <div className="og-block">
          <div className="og-block-hd">
            <span className="t-micro">Uscite</span>
            <span className="og-block-num"><Euro value={u.totale} /></span>
          </div>
          <Riga k="Già pagate" v={u.pagate} natura="FATTO" />
          <Riga k="Fornitori" v={u.dettaglio.fornitori} natura="CERTO" />
          <Riga k="Fisco" v={u.dettaglio.tasse} natura="CERTO" />
          {/* Dentro il Fisco c'è anche quello che NON è ancora un documento:
              l'IVA proiettata sul fatturato, l'F24 sulla media dei dodici mesi.
              Senza questa riga la cifra più grossa della pagina porta scritto
              "certo" e non lo è per intero: a settembre 2026 un quarto di
              quella riga è una stima. Si scrive sotto, come "di cui scaduto". */}
          {u.dettaglio.tasseStima > 0 && (
            <div className="og-row">
              <span className="k">di cui da confermare<Tag natura="PREVISIONE" /></span>
              <span className="n">€ {fmtEur(u.dettaglio.tasseStima)}</span>
            </div>
          )}
          <Riga k="Canoni" v={u.dettaglio.ricorrenti} natura="PREVISIONE" />
          <Riga k="Previsioni a mano" v={u.dettaglio.manuali} natura="PREVISIONE" />
          {u.scaduto > 0 && (
            <div className="og-row">
              <span className="k text-alert">di cui già scaduto</span>
              <span className="n text-alert">€ {fmtEur(u.scaduto)}</span>
            </div>
          )}
        </div>

        <div className="og-saldo">
          <div className="t-micro">Saldo del periodo</div>
          <div className={`og-saldo-num${p.saldo < 0 ? ' text-alert' : ''}`}><Euro value={p.saldo} /></div>
          {/* Non è il saldo sommato alla liquidità: quello che è già stato
              incassato e pagato sta GIÀ dentro il saldo del conto, e risommarlo
              sarebbe lo stesso euro due volte. Si aggiunge solo ciò che deve
              ancora muoversi — il conto lo fa il server, qui si mostra. */}
          <div className="og-row" style={{ marginTop: 10, borderBottom: 'none', paddingBottom: 0 }}>
            <span className="k">Cassa a fine periodo</span>
            <span className={`n font-semibold${p.liquiditaAttesa < 0 ? ' text-alert' : ''}`}><Euro value={p.liquiditaAttesa} /></span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Riga({ k, v, natura }: { k: string; v: number; natura: Natura }) {
  return (
    <div className={`og-row${v === 0 ? ' is-zero' : ''}`}>
      <span className="k">{k}<Tag natura={natura} /></span>
      <span className="n">€ {fmtEur(v)}</span>
    </div>
  );
}

/**
 * Punto 5. Due cose diverse che non vanno sommate alla leggera: il lavoro che
 * abbiamo in mano (contratti, progetti, canoni firmati) e quello che stiamo
 * ancora vendendo (trattative aperte). Sotto, in chiaro, quanto è stato tolto
 * per non contare lo stesso euro due volte.
 */
function Lavoro({ d }: { d: OggiSummary }) {
  const navigate = useNavigate();
  const [proof, setProof] = useState(false);
  const l = d.lavoro;
  const c = d.controllo;
  return (
    <>
      <div className="data-hd">
        <span className="t-micro">Lavoro in mano e lavoro in vendita</span>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setProof((v) => !v)}>
          {proof ? 'Nascondi il conto' : 'Come non contiamo due volte'} <Icon name={proof ? 'chevronU' : 'chevronD'} size={12} />
        </button>
      </div>

      <div className="stat-band og-band4">
        <div>
          <div className="st-label">In mano</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={l.inMano.totale} format={fmtEur} /></div>
          <div className="st-delta">progetti, vinte e canoni firmati</div>
        </div>
        <div className="st-click" role="button" tabIndex={0} onClick={() => navigate('/progetti')} onKeyDown={(ev) => { if (ev.key === 'Enter') navigate('/progetti'); }}>
          <div className="st-label">Progetti da consegnare</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={l.inMano.progetti.valore} format={fmtEur} /></div>
          <div className="st-delta">
            {/* "Nessun progetto aperto" sarebbe falso quando i progetti ci sono
                ma si fatturano tutti a canone: l'elenco Progetti ne mostrerebbe
                cinque e questa riga direbbe zero. */}
            {l.inMano.progetti.count === 0
              ? l.inMano.progettiACanone.count > 0
                ? 'tutti fatturati a canone, qui sotto'
                : 'nessun progetto aperto'
              : `${l.inMano.progetti.count} progett${l.inMano.progetti.count === 1 ? 'o' : 'i'} · residuo da fatturare`}
          </div>
        </div>
        <div>
          <div className="st-label">Canoni, {l.inMano.ricorrente.mesi} mesi</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={l.inMano.ricorrente.valore} format={fmtEur} /></div>
          <div className="st-delta">{l.inMano.ricorrente.count} incassi fino al {fmtDate(l.inMano.ricorrente.fino)}</div>
        </div>
        <div className="st-click" role="button" tabIndex={0} onClick={() => navigate('/trattative')} onKeyDown={(ev) => { if (ev.key === 'Enter') navigate('/trattative'); }}>
          <div className="st-label">In vendita</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={l.inVendita.valore} format={fmtEur} /></div>
          <div className="st-delta">{l.inVendita.count} aperte · pesato € {fmtEur(l.inVendita.pesato)}</div>
        </div>
      </div>

      {l.inMano.progettiACanone.count > 0 && (
        <div className="adm-note">
          <strong>€ {fmtEur(l.inMano.progettiACanone.valore)}</strong>
          <span>
            di progetti ({l.inMano.progettiACanone.count}) non sono in questi totali: si fatturano a rate,
            e le rate sono già contate fra i canoni attivi qui sopra.
          </span>
        </div>
      )}

      {/* Il punto cieco, scritto insieme agli altri e non nascosto. La regola
          "vince la fattura" confronta cliente, mese e importo: sui canoni
          aggregati (GoDeal, Stripe, hosting annuali) il cliente non c'è, e il
          confronto non parte. Se uno di quei clienti ha una fattura aperta, il
          suo pezzo è contato due volte. Va guardato a mano. */}
      {l.inMano.ricorrente.aggregati.valore > 0 && (
        <div className="adm-note">
          <strong>€ {fmtEur(l.inMano.ricorrente.aggregati.valore)}</strong>
          <span>
            dei canoni sono voci aggregate senza cliente ({l.inMano.ricorrente.aggregati.count} incassi):
            su queste il controllo dei doppioni non può scattare, perché confronta il cliente.
            Se un abbonamento lì dentro ha anche una fattura aperta, quel pezzo è contato due volte.
          </span>
        </div>
      )}

      {l.inVendita.rinnovi.count > 0 && (
        <div className="adm-note">
          <strong>€ {fmtEur(l.inVendita.rinnovi.valore)}</strong>
          <span>
            di rinnovi ({l.inVendita.rinnovi.count}) non sono in questi totali: il canone che rinnovano
            è già contato fra i canoni attivi qui sopra.
          </span>
        </div>
      )}

      {proof && <Prova c={c} />}
    </>
  );
}

/**
 * La prova, in euro. Le cinque voci in alto sono disgiunte per costruzione;
 * sotto c'è quanto è stato tolto per renderle tali, con la regola che lo dice e
 * dove è scritta. Sommando alla cieca il totale sarebbe `totaleLordo`, cioè
 * `differenza` euro che non esistono.
 */
function Prova({ c }: { c: OggiSummary['controllo'] }) {
  return (
    <div className="adm-fold">
      <div className="t-micro" style={{ marginBottom: 12 }}>Il conto, voce per voce</div>
      <table className="og-proof">
        <tbody>
          {c.voci.map((v) => (
            <tr key={v.chiave}>
              <td>{v.etichetta}<em className="og-tag">{NATURA_LABEL[v.natura]}</em></td>
              <td className="n">€ {fmtEur(v.valore)}</td>
            </tr>
          ))}
          <tr className="is-total">
            <td>Totale, senza doppioni</td>
            <td className="n">€ {fmtEur(c.totaleNetto)}</td>
          </tr>
        </tbody>
      </table>

      <div className="t-micro" style={{ margin: '22px 0 12px' }}>Tolto perché sarebbe stato contato due volte</div>
      <table className="og-proof">
        <tbody>
          {c.scartato.map((s) => (
            <tr key={s.chiave}>
              <td>
                {s.etichetta} {s.count > 0 && <span className="mute-2">({s.count})</span>}
                <span className="og-why">{s.regola}</span>
              </td>
              <td className="n">− € {fmtEur(s.valore)}</td>
            </tr>
          ))}
          <tr className="is-total">
            <td>
              Sommando alla cieca farebbe € {fmtEur(c.totaleLordo)}
              <span className="og-why">
                {c.quadra
                  ? `€ ${fmtEur(c.totaleLordo)} meno € ${fmtEur(c.totaleNetto)} fa esattamente € ${fmtEur(c.differenza)}: il conto degli scarti torna.`
                  : 'Attenzione: la differenza non coincide con la somma degli scarti.'}
              </span>
            </td>
            <td className={`n${c.quadra ? '' : ' text-alert'}`}>€ {fmtEur(c.differenza)} di troppo</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Punto 6: prima le scadute, poi le prossime. In quest'ordine, sempre. */
function Fatture({ d }: { d: OggiSummary }) {
  const navigate = useNavigate();
  const f = d.fatture;
  const annoOggi = Number(d.today.slice(0, 4));
  const apri = (r: OggiRigaFattura) => navigate(`/admin?tab=incassi${r.numero ? `&q=${encodeURIComponent(r.numero)}` : ''}`);
  return (
    <div className="card oggi-fill">
      <div className="card-hd">
        <div>
          <div className="t-h3">Fatture da incassare</div>
          <div className="mute t-small">
            {f.scadute.count > 0
              ? <>€ {fmtEur(f.scadute.totale)} scaduti su {f.scadute.count + f.prossime.count} rate aperte</>
              : <>nessuna scaduta · {f.prossime.count} rate aperte</>}
          </div>
        </div>
        <Link to="/admin?tab=incassi" className="btn btn-sm btn-ghost">Incassi <Icon name="chevronR" size={12} /></Link>
      </div>
      <div className="card-bd card-bd--tight">
        {f.scadute.count > 0 && (
          <>
            <div className="t-micro text-alert" style={{ padding: '6px 0 2px' }}>Scadute · € {fmtEur(f.scadute.totale)}</div>
            {f.scadute.voci.map((r) => <RigaFatturaView key={r.paymentId} r={r} anno={annoOggi} scaduta onClick={() => apri(r)} />)}
          </>
        )}
        <div className="t-micro" style={{ padding: f.scadute.count > 0 ? '14px 0 2px' : '6px 0 2px' }}>
          In scadenza · € {fmtEur(f.prossime.totale)}
        </div>
        {f.prossime.voci.length === 0
          ? <div className="mute text-[13px] py-3">Nessuna rata in arrivo.</div>
          : f.prossime.voci.map((r) => <RigaFatturaView key={r.paymentId} r={r} anno={annoOggi} onClick={() => apri(r)} />)}
      </div>
    </div>
  );
}

function RigaFatturaView({ r, anno, scaduta, onClick }: { r: OggiRigaFattura; anno: number; scaduta?: boolean; onClick: () => void }) {
  return (
    <div className="adm-line cursor-pointer" role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}>
      <div className={`og-when mono${scaduta ? ' text-alert' : ''}`}>{scadenza(r.dueDate, anno)}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate">{r.cliente}</div>
        <div className="text-[12px] mute">
          {r.numero ?? 'senza numero'}
          {r.rata && ` · rata ${r.rata.seq} di ${r.rata.su}`}
          {scaduta && r.giorniDiRitardo > 0 && ` · ${r.giorniDiRitardo} giorni di ritardo`}
        </div>
      </div>
      <div className={`og-amount${scaduta ? ' text-alert' : ''}`}>€ {fmtEur(r.importo)}</div>
    </div>
  );
}

/**
 * Punto 7. I documenti si contano tutti, senza limite di data: sono debiti che
 * esistono, la data dice solo quando. I canoni no, sono una proiezione: si
 * fermano a una data, ed è scritta.
 */
function DaPagare({ d }: { d: OggiSummary }) {
  const p = d.daPagare;
  return (
    <div className="card oggi-fill">
      <div className="card-hd">
        <div>
          <div className="t-h3">Da pagare</div>
          <div className="mute t-small">
            documenti € {fmtEur(p.documenti.totale)} · canoni € {fmtEur(p.canoni.totale)} fino al {fmtDate(p.canoni.fino)}
          </div>
        </div>
        <Link to="/admin?tab=uscite" className="btn btn-sm btn-ghost">Uscite <Icon name="chevronR" size={12} /></Link>
      </div>
      <div className="card-bd card-bd--tight">
        <div className="og-row"><span className="k">Fornitori<Tag natura="CERTO" /></span><span className="n">€ {fmtEur(p.documenti.fornitori)}</span></div>
        <div className="og-row"><span className="k">Fisco<Tag natura="CERTO" /></span><span className="n">€ {fmtEur(p.documenti.tasse)}</span></div>
        {/* Un terzo del Fisco non è una cartella arrivata ma un numero che ci
            siamo calcolati noi: va detto accanto alla cifra, non nelle note. */}
        {p.documenti.tasseStima > 0 && (
          <div className="og-row"><span className="k">di cui da confermare<Tag natura="PREVISIONE" /></span><span className="n">€ {fmtEur(p.documenti.tasseStima)}</span></div>
        )}
        <div className="og-row"><span className="k">Previsioni a mano<Tag natura="PREVISIONE" /></span><span className="n">€ {fmtEur(p.documenti.manuali)}</span></div>
        <div className="og-row"><span className="k">Canoni fino al {fmtDate(p.canoni.fino)}<Tag natura="PREVISIONE" /></span><span className="n">€ {fmtEur(p.canoni.totale)}</span></div>
        {p.documenti.scaduto > 0 && (
          <div className="og-row"><span className="k text-alert">di cui già scaduto</span><span className="n text-alert">€ {fmtEur(p.documenti.scaduto)}</span></div>
        )}

        <div className="t-micro" style={{ padding: '16px 0 2px' }}>Le prossime</div>
        {p.voci.length === 0
          ? <div className="mute text-[13px] py-3">Non c'è niente da pagare.</div>
          : p.voci.map((v) => <RigaPagamentoView key={`${v.fonte}-${v.id}`} v={v} anno={Number(d.today.slice(0, 4))} />)}
      </div>
    </div>
  );
}

function RigaPagamentoView({ v, anno }: { v: OggiRigaPagamento; anno: number }) {
  const tardi = v.giorniDiRitardo > 0;
  return (
    <div className="adm-line">
      <div className={`og-when mono${tardi ? ' text-alert' : ''}`}>{scadenza(v.dueDate, anno)}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate">{v.controparte}</div>
        <div className="text-[12px] mute truncate">
          {FONTE_LABEL[v.fonte]}
          {v.descrizione && ` · ${v.descrizione}`}
          {!v.documento && ' · previsione'}
          {tardi && ` · ${v.giorniDiRitardo} giorni di ritardo`}
        </div>
      </div>
      <div className={`og-amount${tardi ? ' text-alert' : ''}`}>€ {fmtEur(v.importo)}</div>
    </div>
  );
}

function NoSoldi() {
  return (
    <div className="card">
      <div className="card-bd text-[13px] mute">
        I numeri di cassa sono riservati a chi ha l'Amministrazione. Qui sotto restano le cose da fare.
      </div>
    </div>
  );
}

function Scheletro({ loading }: { loading: boolean }) {
  if (!loading) return null;
  return (
    <>
      <div className="stat-band">
        {[0, 1, 2, 3].map((i) => (
          <div key={i}><span className="skeleton sk-sm" style={{ maxWidth: 110 }} /><span className="skeleton sk-lg" style={{ maxWidth: 160 }} /></div>
        ))}
      </div>
      <div className="og-periods">
        {[0, 1, 2].map((i) => (
          <div key={i} className="card"><div className="card-bd skeleton-col">
            <span className="skeleton sk-block" /><span className="skeleton sk-block" /><span className="skeleton sk-block" />
          </div></div>
        ))}
      </div>
    </>
  );
}
